import { useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api";
import { ApiError } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { InviteDialog } from "../components/InviteDialog";
import { MemberRow } from "../components/MemberRow";
import { Button } from "../compose/controls";
import { Search, ShieldCheck, UserPlus, Users } from "../compose/Icon";
import { EmptyState, ErrorState, SkeletonRows } from "../compose/states";
import { useAsync } from "../lib/useAsync";
import { useDebouncedValue } from "../lib/useDebouncedValue";

/**
 * People `/w/:slug/people` (design screens §people). Staff-only member
 * management: a live, debounced search over the workspace's members (server-side
 * `?q=` on name/email/role) + an Invite action that mints a set-password link to
 * copy and hand off. Non-staff see an honest, access-scoped empty state; a query
 * with no match teaches how to recover. The invite flow is unchanged.
 */
export function People() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { user, memberships } = useAuth();

  // WHO may manage membership. Derived from the caller's REAL authority — the
  // same predicate `Settings.tsx` uses for the workspace section — NOT from
  // "the list call did not 403". Any member who can read the roster can read
  // it; only global staff or a `staff` membership in THIS workspace may write
  // it, and the server is authoritative either way. Deriving this from the read
  // handed every viewer a role dropdown + Remove button the server always
  // rejected.
  const role = memberships.find((m) => m.workspace_slug === slug)?.role;
  const canManage = Boolean(user?.is_staff) || role === "staff";
  const [inviteOpen, setInviteOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query.trim(), 250);
  const searching = debounced.length > 0;

  // Server-side filter: refetch when the debounced term settles (mirrors the
  // document-list precedent). Empty term ⇒ the full membership.
  const people = useAsync(
    () => api.listPeople(slug, debounced || undefined),
    [slug, debounced],
  );

  const forbidden =
    people.error instanceof ApiError && people.error.status === 403;

  const count = people.data?.length ?? 0;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">People</h1>
          <p className="page__sub">
            Members of this workspace and their roles.
          </p>
        </div>
        {canManage ? (
          <Button
            variant="primary"
            data-testid="invite-person"
            icon={<UserPlus size={15} strokeWidth={1.5} />}
            onClick={() => setInviteOpen(true)}
          >
            Invite person
          </Button>
        ) : null}
      </div>

      {!forbidden ? (
        <div className="docbar">
          <div className="search">
            <Search className="search__icon" size={15} strokeWidth={1.5} aria-hidden />
            <input
              type="search"
              className="search__input"
              data-testid="people-search"
              aria-label="Search people by name, email, or role"
              placeholder="Search name, email, or role…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <span className="page__sub" style={{ margin: 0 }} data-testid="people-count">
            {people.data
              ? `${count} ${count === 1 ? "member" : "members"}${searching ? " match" : ""}`
              : ""}
          </span>
        </div>
      ) : null}

      {people.loading ? (
        <div className="people">
          <SkeletonRows rows={3} />
        </div>
      ) : forbidden ? (
        <EmptyState
          icon={<ShieldCheck size={30} strokeWidth={1.5} />}
          title="Staff only"
          body="Member management is available to workspace staff. Ask an admin to change a role."
        />
      ) : people.error ? (
        <ErrorState body="We couldn't load the member list." />
      ) : people.data && people.data.length > 0 ? (
        <div className="people" data-testid="people-list">
          {people.data.map((p) => (
            <MemberRow
              key={p.user.id}
              person={p}
              slug={slug}
              canManage={canManage}
              currentUserId={user?.id ?? ""}
              onChanged={() => people.reload()}
            />
          ))}
        </div>
      ) : searching ? (
        <EmptyState
          icon={<Search size={30} strokeWidth={1.5} />}
          title="No people match"
          body={`No members match “${debounced}”. Try a different name, email, or role.`}
          action={
            <Button
              variant="secondary"
              data-testid="people-search-clear"
              onClick={() => setQuery("")}
            >
              Clear search
            </Button>
          }
        />
      ) : (
        <EmptyState
          icon={<Users size={30} strokeWidth={1.5} />}
          title="No members yet"
          body="Invite the first person to this workspace."
        />
      )}

      {inviteOpen ? (
        <InviteDialog
          slug={slug}
          onClose={() => setInviteOpen(false)}
          onInvited={() => people.reload()}
        />
      ) : null}
    </div>
  );
}
